import React from "react";
import {VivoPortal} from "@utils/AppConstants";
import {canAccessCustomerManagement} from "@utils/auth/customerManagementAccess";

const EnumMemberGroupAliases = VivoPortal.EnumMemberGroupAliases;
const GccLabId = VivoPortal.GccLabId;
const ProductCode_NaLabs = VivoPortal.ProductCode_NaLabs;

const LoginPage = React.lazy(() => import("@pages/login/LoginPage"));
const LogoutComponent = React.lazy(() => import("@pages/login/LogoutComponent"));
const MyFilesPage = React.lazy(() => import("@pages/other/myfiles/MyFilesPage"));
const CustomerListPage = React.lazy(() => import("@pages/administration/customers/CustomerListPage"));
const ProductCodeListPage = React.lazy(() => import("@pages/product_codes/list/ProductCodeListPage"));
const ProductCodeEditPage = React.lazy(() => import("@pages/product_codes/edit/ProductCodeEditPage"));
const RecipeListPage = React.lazy(() => import("@pages/recipes/list/RecipeListPage"));
const RecipeEditPage = React.lazy(() => import("@pages/recipes/edit/RecipeEditPage"));
const RecipePreviewPage = React.lazy(() => import("@pages/recipes/preview/RecipePreviewPage"));
const InkSeriesEditPage = React.lazy(() => import("@pages/administration/InkSeriesEditPage"));
const ProjectListPage = React.lazy(() => import("@pages/orders/list/ProjectListPage"));
const OrderListPage = React.lazy(() => import("@pages/orders/list/OrderListPage"));
const OrderEditPage = React.lazy(() => import("@pages/orders/edit/OrderEditPage"));
const OrderTemplateSelectionPreviewPage = React.lazy(() => import("@pages/orders/template_selection_preview/OrderTemplateSelectionPreviewPage"));
const OrderPreviewPage = React.lazy(() => import("@pages/orders/preview/OrderPreviewPage"));
const OrderPrintPage = React.lazy(() => import("@pages/orders/print/OrderPrintPage"));
const OrderSerialPrintPage = React.lazy(() => import("@pages/orders/print/OrderSerialPrintPage"));
const UserListPage = React.lazy(() => import("@pages/administration/users/UserListPage"));
const UserProfileEditPage = React.lazy(() => import("@pages/administration/users/edit/UserProfileEditPage"));
const UserSettingsEditPage = React.lazy(() => import("@pages/administration/users/settings/UserSettingsEditPage"));
const ComponentListPage = React.lazy(() => import("@pages/administration/components/ComponentListPage"));
const InkSeriesListPage = React.lazy(() => import("@pages/administration/InkSeriesListPage"));
const CustomerEditPage = React.lazy(() => import("@pages/administration/customers/edit/CustomerEditPage"));
const RecipePrintPage = React.lazy(() => import("@pages/recipes/print/RecipePrintPage"));
const LabEditPage = React.lazy(() => import("@pages/administration/laboratories/LabEditPage"));
const EmailListPage = React.lazy(() => import("@pages/administration/mailings/EmailListPage"));
const EmailGroupListPage = React.lazy(() => import("@pages/administration/emailgroups/EmailGroupListPage"));
const EmailGroupDetailsPage = React.lazy(() => import("@pages/administration/emailgroups/edit/EmailGroupDetailsPage"));
const ChartJobStatisticsPage = React.lazy(() => import("@pages/charts/job_statistics/ChartJobStatisticsPage"));
const ChartJobsPercentagePage = React.lazy(() => import("@pages/charts/job_percentage/ChartJobsPercentagePage"));
const DelayStatisticsPage = React.lazy(() => import("@pages/charts/delay_statistics/DelayStatisticsPage"));
const OrderListReportPrintPage = React.lazy(() => import("@pages/reports/OrderListReportPrintPage"));
const CsvReportsPage = React.lazy(() => import("@pages/reports/CsvReportPage"));
const BrandColorReportPage = React.lazy(() => import("@pages/reports/BrandColorReportPage"));
const RftMarsChartPage = React.lazy(() => import("@pages/charts/rft_chart/RftMarsChartPage"));
const JobAssignmentListPage = React.lazy(() => import("@pages/other/job_assignment/JobAssignmentListPage"));
const EmailDetailsPage = React.lazy(() => import("@pages/administration/mailings/EmailDetailsPage"));
const WorkEvaluationListPage = React.lazy(() => import("@pages/work_evaluation/list/WorkEvaluationListPage"));
const WorkEvaluationEditPage = React.lazy(() => import("@pages/work_evaluation/edit/WorkEvaluationEditPage"));
const DropDownListManagementPage = React.lazy(() => import("@pages/administration/drop_down_list_management/DropDownListManagementPage"));
const RezConverterPage = React.lazy(() => import("@pages/other/rez_converter/RezConverterPage"));
const OrderRecipeListPage = React.lazy(() => import("@pages/orders/order_recipe_list/OrderRecipeListPage"));
const OrderRecipePrintPage = React.lazy(() => import("@pages/orders/order_recipe_list/print/OrderRecipePrintPage"));
const UserPreRegistrationPage = React.lazy(() => import("@pages/administration/internal_user_registration/UserPreRegistrationPage"));
const SimpleProofLabelPage = React.lazy(() => import("@pages/orders/proof_labels/simple/SimpleProofLabelPage"));
const ComplexProofLabelPage = React.lazy(() => import("@pages/orders/proof_labels/complex/ComplexProofLabelPage"));
const GccProofLabelPage = React.lazy(() => import("@pages/orders/proof_labels/gcc/GccProofLabelPage"));
const AnnouncementListPage = React.lazy(() => import("@pages/administration/announcements/AnnouncementListPage"));
const AnnouncementEditPage = React.lazy(() => import("@pages/administration/announcements/AnnouncementEditPage"));
const AboutUsPage = React.lazy(() => import("@pages/about/AboutUsPage"));
const DashboardPage = React.lazy(() => import("@pages/dashboard/DashboardPage"));
 
const routes = [
    {path: "/:lng/login", element: <LoginPage/>},
    {path: "/:lng/logout", element: <LogoutComponent/>},
    // { path: "/login", element: <LoginPage /> },
    {
        path: "/:lng/orders",
        element: <OrderListPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "Order List Page",
        titleKey: "NAV_OrderList"
    },
    {
        path: "/:lng/projects",
        element: <ProjectListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        labFilter: [1, 11,19],
        piwikName: "Project List Page",
        titleKey: "NAV_ProjectList"
    },
    {
        path: `/:lng/orders/edit/:guid?`,
        element: <OrderEditPage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Order Edit Page",
        titleKey: "ORDER_EDIT_PageTitle"
    },
    // PREVIEW ONLY: mock-data prototype of the New Order start screen. Deliberately no menu entry; explicit non-zero roleMask (same as Order Edit).
    {
        path: `/:lng/previews/order-template-selection`,
        element: <OrderTemplateSelectionPreviewPage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Order Template Selection Preview",
        titleKey: "ORDER_TEMPLATE_PREVIEW_PageTitle"
    },
    {
        path: `/:lng/orders/print/:guid`,
        element: <OrderPrintPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "Order Print Preview Page",
        titleKey: "ORDER_PRINT_PageTitle"
    },
    {
        path: `/:lng/order-recipes`,
        element: <OrderRecipeListPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Order Recipe List Page",
        titleKey: "ORDER_RECIPES_PageTitle"
    },
    {
        path: `/:lng/order-recipes/print/:guid`,
        element: <OrderRecipePrintPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Order Recipe Print Page",
        titleKey: "PAGE_TITLE_ORDER_RECIPE_Print"
    },
    {
        path: `/:lng/orders/serial-print/:oid`,
        element: <OrderSerialPrintPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "Order Serial Print Preview Page",
        titleKey: "ORDER_PRINT_Serial_PageTitle"
    },
    {
        path: `/:lng/orders/preview/:guid`,
        element: <OrderPreviewPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "Order Preview Page",
        titleKey: "ORDER_PREVIEW_PageTitle"
    },
    {
        path: `/:lng/orders/labels/simple/:guid`,
        element: <SimpleProofLabelPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Simple Proof Label",
        titleKey: "ORDER_LABELS_SimpleProofLabelPageTitle"
    },
    {
        path: `/:lng/orders/labels/complex/:guid`,
        element: <ComplexProofLabelPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Complex Proof Label",
        titleKey: "ORDER_LABELS_ComplexProofLabelPageTitle"
    },
    {
        path: `/:lng/orders/labels/gcc/:guid`,
        element: <GccProofLabelPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Gcc Proof Label",
        titleKey: "ORDER_LABELS_GccProofLabelPageTitle"
    },
    {
        path: "/:lng/files",
        element: <MyFilesPage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Attachment List Page",
        titleKey: "NAV_MyFiles"
    },
    {
        path: "/:lng/product-codes",
        element: <ProductCodeListPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.SuperAdmin,
        labFilter: [GccLabId, ...ProductCode_NaLabs],
        piwikName: "Product Codes",
        titleKey: "NAV_CODES"
    },
    {
        path: "/:lng/product-codes/edit/:guid?",
        element: <ProductCodeEditPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.SuperAdmin,
        labFilter: [GccLabId, ...ProductCode_NaLabs],
        piwikName: "Product Codes Edit",
        titleKey: "CODES_EDIT_PageTitle"
    },
    {
        path: "/:lng/product-codes/inherit/:parentGuid",
        element: <ProductCodeEditPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.SuperAdmin,
        labFilter: [GccLabId, ...ProductCode_NaLabs],
        piwikName: "Product Codes Edit",
        titleKey: "CODES_EDIT_PageTitle"
    },
    {
        path: "/:lng/users",
        element: <UserListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "User Management",
        titleKey: "NAV_USER_MANAGEMENT"
    },
    {
        path: `/:lng/users/edit/:guid`,
        element: <UserProfileEditPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "User Edit Page",
        titleKey: "USER_PROFILE_PageTitle"
    },
    {
        path: `/:lng/users/settings/:guid`,
        element: <UserSettingsEditPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "User Settings Edit Page",
        titleKey: "USER_SETTINGS_PageTitle"
    },
    {
        path: "/:lng/customers",
        element: <CustomerListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Employee,
        accessRule: canAccessCustomerManagement,
        piwikName: "Customer List Page",
        titleKey: "NAV_CUST_MANAGEMENT"
    },
    {
        path: `/:lng/customers/edit/:guid?`,
        element: <CustomerEditPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Employee,
        accessRule: canAccessCustomerManagement,
        piwikName: "Customer Edit Page",
        titleKey: "CUSTOMER_EditTitle"
    },
    {
        path: "/:lng/components",
        element: <ComponentListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Component Management Page",
        titleKey: "NAV_COMPONENTS"
    },
    {
        path: "/:lng/laboratories",
        element: <LabEditPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Laboratory Management Page",
        titleKey: "NAV_LAB_MANAGEMENT"
    },
    {
        path: "/:lng/email-groups",
        element: <EmailGroupListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Email Group Management Page",
        titleKey: "EmailGroupManagement"
    },
    {
        path: "/:lng/email-groups/edit/:guid?",
        element: <EmailGroupDetailsPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Email Group Details Page",
        titleKey: "lblEmailGroup"
    },
    {
        path: "/:lng/ink-series",
        element: <InkSeriesListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Ink Series Management Page",
        titleKey: "NAV_INK_SERIES"
    },
    {
        path: `/:lng/ink-series/edit/:guid?`,
        element: <InkSeriesEditPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Ink Series Edit Page",
        titleKey: "SERIES_PAGE_TITLE_Editing"
    },
    {
        path: "/:lng/recipes",
        element: <RecipeListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Recipe List Page",
        titleKey: "PAGE_TITLE_RECIPE_LIST"
    },
    {
        path: `/:lng/recipes/preview/:guid`,
        element: <RecipePreviewPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Recipe Preview Page",
        titleKey: "PAGE_TITLE_RECIPE_Edit"
    },
    {
        path: `/:lng/recipes/edit/:guid?`,
        element: <RecipeEditPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Recipe Edit Page",
        titleKey: "PAGE_TITLE_RECIPE_Preview"
    },
    {
        path: `/:lng/recipes/print/:guid`,
        element: <RecipePrintPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Recipe Print Page",
        titleKey: "PAGE_TITLE_RECIPE_Print"
    },
    {
        path: `/:lng/charts/job-statistics`,
        element: <ChartJobStatisticsPage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Job Statistics Page",
        titleKey: "NAV_JOB_STAT"
    },
    {
        path: `/:lng/charts/jobs-percentage`,
        element: <ChartJobsPercentagePage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Job Percentage Page",
        titleKey: "NAV_JOB_PERCENT"
    },
    {
        path: `/:lng/charts/delay-statistics`,
        element: <DelayStatisticsPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Job Delay Page",
        titleKey: "NAV_DELAY_STAT"
    },
    {
        path: `/:lng/reports/order-list-report`,
        element: <OrderListReportPrintPage/>,
        roleMask: EnumMemberGroupAliases.Customer | EnumMemberGroupAliases.Employee | EnumMemberGroupAliases.Supervisor | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Order List Report Page",
        titleKey: "PAGE_TITLE_OrderListReport"
    },
    {
        path: `/:lng/reports/csv-reports`,
        element: <CsvReportsPage/>,
        roleMask: EnumMemberGroupAliases.Admin,
        piwikName: "CSV Report Page",
        titleKey: "REPORTS_CSV"
    },
    {
        path: `/:lng/reports/brand-color-report`,
        element: <BrandColorReportPage/>,
        roleMask: EnumMemberGroupAliases.Admin,
        labFilter: [1],
        divisionFilter: [1],
        piwikName: "Brand Color Report Page",
        titleKey: "NAV_BRAND_COLOR_REPORT"
    },
    {
        path: `/:lng/charts/rft-chart`,
        element: <RftMarsChartPage/>,
        roleMask: EnumMemberGroupAliases.Admin,
        labFilter: [1],
        divisionFilter: [1],
        piwikName: "RFT Mars Report Page",
        titleKey: "NAV_RFT_MARS_REPORTS"
    },
    {
        path: `/:lng/job-assignment-list`,
        element: <JobAssignmentListPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Job Assignment List Page",
        titleKey: "NAV_JOBS_ASSIGNMENT_LIST"
    },
    {
        path: "/:lng/email-list",
        element: <EmailListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Email List Page",
        titleKey: "MailingList"
    },
    {
        path: "/:lng/email-list/preview/:guid?",
        element: <EmailDetailsPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "Email Details Page",
        titleKey: "RecipientList"
    },
    {
        path: "/:lng/work-evaluation-list", element: <WorkEvaluationListPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Work Evaluation List Page",
        titleKey: "NAV_WORK_EVAL"
    },
    {
        path: "/:lng/work-evaluation-list/edit/:guid?", element: <WorkEvaluationEditPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        piwikName: "Activity Edit Page",
        titleKey: "PAGE_TITLE_WorkEvaluationEdit"
    },
    {
        path: "/:lng/drop-down-list-management", element: <DropDownListManagementPage/>,
        roleMask: EnumMemberGroupAliases.SuperAdmin | EnumMemberGroupAliases.Admin,
        piwikName: "DropDown Management Page",
        titleKey: "NAV_DROPDOWNLIST_MANAGEMENT"
    },
    {
        path: "/:lng/rez-converter",
        element: <RezConverterPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.Supervisor,
        labFilter: [1],
        piwikName: "Rez Converter Page",
        titleKey: "NAV_REZCONVERTER"
    },
    {
        path: "/:lng/pre-register-new-user-19287344",
        element: <UserPreRegistrationPage/>,
        roleMask: EnumMemberGroupAliases.Admin,
        piwikName: "User Preregistration Page",
        titleKey: "NAV_USER_PREREGISTRATION"
    },
    {
        path: "/:lng/announcements",
        element: <AnnouncementListPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "Announcement List Page",
        titleKey: "ANNOUNCEMENT_PageTitle"
    },
    {
        path: "/:lng/announcements/edit",
        element: <AnnouncementEditPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "announcements-edit",
        titleKey: "ANNOUNCEMENT_Editor_TitleCreate"
    },
    {
        path: "/:lng/announcements/edit/:guid",
        element: <AnnouncementEditPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "announcements-edit",
        titleKey: "ANNOUNCEMENT_Editor_TitleEdit"
    },
    {
        path: "/:lng/about",
        element: <AboutUsPage/>,
        roleMask: EnumMemberGroupAliases.Admin | EnumMemberGroupAliases.SuperAdmin,
        piwikName: "About Us Page",
        titleKey: "NAV_About"
    },
    {
        path: "/:lng/dashboard",
        element: <DashboardPage/>,
        piwikName: "Dashboard Page",
        titleKey: "PAGE_TITLE_Dashboard"
    },
    {
        path: "/:lng/",
        element: <OrderListPage/>,
        roleMask: EnumMemberGroupAliases.User,
        piwikName: "Home",
        titleKey: "COMMON_Home"
    }
];

export default routes;
